import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Eraser } from 'lucide-react'
import { toast } from 'sonner'

import { getPageHeader } from '@/constants'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { PageHeaderStack } from '@/components/navigation/page-header-stack'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  useDeleteSimulatedRows,
  useFlagSimulationRows,
  useSimulationCleanup,
  type SimulationCleanupOrganisation,
  type SimulationCleanupRun,
  type SimulationCleanupSummary,
  type SimulationModelCounts,
} from '@/hooks/use-platform-simulation'

const MODEL_LABELS: Record<string, string> = {
  user: 'Users',
  profile: 'Profiles',
  organisation: 'Organisations',
  opportunity: 'Jobs',
  application: 'Applications',
}

const ACTION_LABELS: Record<SimulationCleanupRun['action'], string> = {
  flag: 'Flag',
  delete: 'Delete',
  mark_selected: 'Mark selected',
  delete_selected: 'Delete selected',
}

function OrganisationNames({
  title,
  rows,
  empty,
}: {
  title: string
  rows: SimulationCleanupOrganisation[]
  empty: string
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">
        {title} ({rows.length})
      </h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto text-sm">
          {rows.map((row) => (
            <li key={row.id} className="flex flex-wrap justify-between gap-2">
              <span>{row.name}</span>
              <span className="text-muted-foreground">{row.reason}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function total(counts: SimulationModelCounts): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0)
}

function CountsTable({ summary }: { summary: SimulationCleanupSummary }) {
  const models = Object.keys(MODEL_LABELS).filter(
    (model) =>
      model in summary.flagged || model in summary.would_flag || model in summary.would_delete,
  )
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Record</TableHead>
          <TableHead className="text-right">Flagged now (hidden)</TableHead>
          <TableHead className="text-right">Unflagged, would be flagged</TableHead>
          <TableHead className="text-right">Would be deleted</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {models.map((model) => (
          <TableRow key={model}>
            <TableCell>{MODEL_LABELS[model]}</TableCell>
            <TableCell className="text-right tabular-nums">{summary.flagged[model] ?? 0}</TableCell>
            <TableCell className="text-right tabular-nums">{summary.would_flag[model] ?? 0}</TableCell>
            <TableCell className="text-right tabular-nums">
              {model === 'user' ? '—' : (summary.would_delete[model] ?? 0)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function CountNotes({ summary }: { summary: SimulationCleanupSummary }) {
  const skipped = total(summary.would_flag_skipped)
  return (
    <ul className="space-y-1 text-sm text-muted-foreground">
      <li>Delete removes the user behind each deleted profile.</li>
      {skipped > 0 ? (
        <li>
          {skipped} matching {skipped === 1 ? 'row is' : 'rows are'} left unflagged because a real
          member or job uses it, or it is the tenant organisation.
        </li>
      ) : null}
      {summary.would_delete_refused > 0 ? (
        <li>{summary.would_delete_refused} flagged rows are kept by the delete rules.</li>
      ) : null}
      {summary.flagged_users_without_profile > 0 ? (
        <li>
          {summary.flagged_users_without_profile} flagged users have no profile, so delete keeps
          them. They stay hidden.
        </li>
      ) : null}
      {summary.unmatched_reserved_email_users > 0 ? (
        <li>
          {summary.unmatched_reserved_email_users} other users have placeholder addresses
          (.invalid or example.com) that are not simulation markers. Flag and delete leave them.
        </li>
      ) : null}
    </ul>
  )
}

function RunLog({ runs }: { runs: SimulationCleanupRun[] }) {
  if (runs.length === 0) {
    return <p className="text-sm text-muted-foreground">No flag or delete runs yet.</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>When</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>By</TableHead>
          <TableHead>Result</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.map((run) => (
          <TableRow key={run.id}>
            <TableCell className="whitespace-nowrap">{new Date(run.created_at).toLocaleString()}</TableCell>
            <TableCell>{ACTION_LABELS[run.action] ?? run.action}</TableCell>
            <TableCell>{run.actor_email || 'Unknown'}</TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={run.status === 'succeeded' ? 'secondary' : 'destructive'}>
                  {run.status === 'succeeded' ? 'Succeeded' : 'Failed'}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {Object.entries(run.counts)
                    .map(([model, n]) => `${MODEL_LABELS[model] ?? model} ${n}`)
                    .join(', ')}
                  {run.targets.length
                    ? `: ${run.targets
                        .filter((row) => row.changes)
                        .map((row) => row.label)
                        .join(', ')}`
                    : ''}
                  {run.detail ? ` — ${run.detail}` : ''}
                </span>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function FlagAction({ summary }: { summary: SimulationCleanupSummary }) {
  const flag = useFlagSimulationRows()
  const [open, setOpen] = useState(false)
  const count = total(summary.would_flag)

  async function onConfirm() {
    try {
      const run = await flag.mutateAsync()
      toast.success(`Flagged ${run.counts.written ?? 0} rows`)
      setOpen(false)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not flag rows')
    }
  }

  return (
    <EntityManageSection
      title="Flag simulation rows"
      subtitle="Marks the unflagged rows counted above as simulated, so Platform lists and reports hide them. Nothing is deleted."
    >
      <Button type="button" disabled={count === 0} onClick={() => setOpen(true)}>
        Flag {count} {count === 1 ? 'row' : 'rows'}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Flag {count} rows as simulated?</AlertDialogTitle>
            <AlertDialogDescription>
              They stay in the database and disappear from Platform lists, reports and search. The run is logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button type="button" disabled={flag.isPending} onClick={() => void onConfirm()}>
              {flag.isPending ? 'Flagging…' : 'Flag rows'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EntityManageSection>
  )
}

function DeleteAction({ summary }: { summary: SimulationCleanupSummary }) {
  const remove = useDeleteSimulatedRows()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const count = total(summary.would_delete)
  const phrase = summary.delete_confirmation

  function onOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setTyped('')
  }

  async function onConfirm() {
    try {
      await remove.mutateAsync(typed)
      toast.success('Simulated rows deleted')
      onOpenChange(false)
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not delete rows')
    }
  }

  return (
    <EntityManageSection
      title="Delete simulated rows"
      subtitle="Hard-deletes every flagged profile, organisation, job and application the purge rules allow. The tenant organisation is kept. This cannot be undone."
    >
      <Button type="button" variant="destructive" disabled={count === 0} onClick={() => setOpen(true)}>
        Delete {count} {count === 1 ? 'row' : 'rows'}
      </Button>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {count} simulated rows?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Only flagged rows are deleted, and the run is logged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="simulation-cleanup-confirm">Type {phrase} to confirm</Label>
            <Input
              id="simulation-cleanup-confirm"
              value={typed}
              autoComplete="off"
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending || typed.trim() !== phrase}
              onClick={() => void onConfirm()}
            >
              {remove.isPending ? 'Deleting…' : 'Delete rows'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </EntityManageSection>
  )
}

export function PlatformSimulationCleanupPage() {
  const { data: summary, isLoading, error } = useSimulationCleanup()

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          {...getPageHeader(
            '/platform/simulation-cleanup',
            'Simulation cleanup',
            'Find rows made by simulation tools, flag them so lists hide them, or delete them.',
          )}
          icon={<Eraser className="h-5 w-5" />}
        />
      </PageHeaderStack>

      {isLoading ? <p className="text-sm text-muted-foreground">Counting rows…</p> : null}
      {error ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Could not load counts'}
        </p>
      ) : null}
      {summary ? (
        <div className="space-y-6">
          <EntityManageSection
            title="Counts (dry run)"
            subtitle="Counting writes nothing. Flagged rows are already hidden from Platform lists."
          >
            <div className="space-y-4">
              <CountsTable summary={summary} />
              <CountNotes summary={summary} />
            </div>
          </EntityManageSection>
          <EntityManageSection
            title="Organisations"
            subtitle="Flag catches only organisations with a simulation name, slug or run record. Mark the rest by hand: select them on the Organisations page and choose Mark as simulated."
          >
            <div className="grid gap-6 md:grid-cols-2">
              <OrganisationNames
                title="Flag would mark"
                rows={summary.would_flag_organisations}
                empty="No unflagged organisations match."
              />
              <OrganisationNames
                title="Flag leaves alone"
                rows={summary.skipped_organisations}
                empty="None."
              />
            </div>
            <Button asChild variant="outline" size="sm" className="mt-4">
              <Link to="/platform/organisations">Open Organisations</Link>
            </Button>
          </EntityManageSection>
          <FlagAction summary={summary} />
          <DeleteAction summary={summary} />
          <EntityManageSection title="Action log" subtitle="Every flag and delete run, newest first.">
            <RunLog runs={summary.runs} />
          </EntityManageSection>
        </div>
      ) : null}
    </PageContainer>
  )
}
